import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { CheckCircle, AlertTriangle, XCircle, BookOpen, Sparkles, Star, HelpCircle } from 'lucide-react';

const MoveQualityCard = () => {
  const { 
    lastMoveQuality, 
    boardTheme,
    gameStatus,
    history
  } = useGameStore();
  
  // Show placeholder when no move made yet
  if (gameStatus !== 'playing' || history.length === 0) {
    return (
      <div className={`
        p-4 rounded-xl
        ${boardTheme === 'cosmic'
          ? 'bg-slate-900/80 border border-purple-500/30'
          : 'bg-white/80 border border-amber-200'
        }
      `}>
        <div className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="w-4 h-4" />
            <span className="font-medium">Move Quality</span>
          </div>
          <p className="text-xs">Your move quality will appear here after you make a move.</p>
        </div>
      </div>
    );
  }
  
  // If no analysis yet (AI move just happened)
  if (!lastMoveQuality) {
    return (
      <div className={`
        p-4 rounded-xl
        ${boardTheme === 'cosmic'
          ? 'bg-slate-900/80 border border-purple-500/30'
          : 'bg-white/80 border border-amber-200'
        }
      `}>
        <div className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>Waiting for your move...</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Icon based on quality
  const getIcon = () => {
    switch (lastMoveQuality.quality) {
      case 'brilliant':
        return <Sparkles className="w-5 h-5" />;
      case 'great':
        return <Star className="w-5 h-5" />;
      case 'good':
        return <CheckCircle className="w-5 h-5" />;
      case 'book':
        return <BookOpen className="w-5 h-5" />;
      case 'inaccuracy':
        return <AlertTriangle className="w-5 h-5" />;
      case 'mistake':
        return <AlertTriangle className="w-5 h-5" />;
      case 'blunder':
        return <XCircle className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };
  
  const qualityStyles = {
    brilliant: {
      bg: boardTheme === 'cosmic' ? 'bg-cyan-900/30' : 'bg-cyan-50',
      border: 'border-cyan-500/50',
      text: 'text-cyan-400',
      accent: boardTheme === 'cosmic' ? 'bg-cyan-500/20' : 'bg-cyan-100'
    },
    great: {
      bg: boardTheme === 'cosmic' ? 'bg-green-900/30' : 'bg-green-50',
      border: 'border-green-500/50',
      text: 'text-green-400',
      accent: boardTheme === 'cosmic' ? 'bg-green-500/20' : 'bg-green-100'
    },
    good: {
      bg: boardTheme === 'cosmic' ? 'bg-lime-900/30' : 'bg-lime-50',
      border: 'border-lime-500/50',
      text: 'text-lime-400',
      accent: boardTheme === 'cosmic' ? 'bg-lime-500/20' : 'bg-lime-100'
    },
    book: {
      bg: boardTheme === 'cosmic' ? 'bg-purple-900/30' : 'bg-purple-50',
      border: 'border-purple-500/50',
      text: 'text-purple-400',
      accent: boardTheme === 'cosmic' ? 'bg-purple-500/20' : 'bg-purple-100'
    },
    inaccuracy: {
      bg: boardTheme === 'cosmic' ? 'bg-yellow-900/30' : 'bg-yellow-50',
      border: 'border-yellow-500/50',
      text: 'text-yellow-400',
      accent: boardTheme === 'cosmic' ? 'bg-yellow-500/20' : 'bg-yellow-100'
    },
    mistake: {
      bg: boardTheme === 'cosmic' ? 'bg-orange-900/30' : 'bg-orange-50',
      border: 'border-orange-500/50',
      text: 'text-orange-400',
      accent: boardTheme === 'cosmic' ? 'bg-orange-500/20' : 'bg-orange-100'
    },
    blunder: {
      bg: boardTheme === 'cosmic' ? 'bg-red-900/30' : 'bg-red-50',
      border: 'border-red-500/50',
      text: 'text-red-400',
      accent: boardTheme === 'cosmic' ? 'bg-red-500/20' : 'bg-red-100'
    }
  };
  
  const style = qualityStyles[lastMoveQuality.quality] || qualityStyles.good;
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={lastMoveQuality.actualMove}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
        className={`
          p-4 rounded-xl border
          ${style.bg} ${style.border}
        `}
        data-testid="move-quality-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${style.accent}`}>
              <span className={style.text}>{getIcon()}</span>
            </div>
            <div>
              <div className={`font-bold ${style.text}`}>
                {lastMoveQuality.label}
              </div>
              <div className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                You played: <span className="font-mono">{lastMoveQuality.actualMove}</span>
              </div>
            </div>
          </div>
          <span className="text-2xl">{lastMoveQuality.icon}</span>
        </div>
        
        {/* Explanation */}
        <p className={`text-sm mb-3 ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
          {lastMoveQuality.explanation}
        </p>
        
        {/* Better move hint */}
        {!lastMoveQuality.isBestMove && lastMoveQuality.suggestedMove && (
          <div className={`
            text-xs p-2 rounded-lg
            ${boardTheme === 'cosmic' ? 'bg-slate-800/50 text-gray-400' : 'bg-gray-100 text-gray-600'}
          `}>
            💡 Better move: <span className="font-mono font-medium">{lastMoveQuality.suggestedMove}</span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default MoveQualityCard;
